import React, { useState, useEffect, useCallback, useRef } from 'react';
import { accountsAPI, transactionAPI } from '../../services/api';
import { listCategories } from '../../services/categories';
import useTransactions from '../../hooks/useTransactions';
import {
  Chart,
  CategoryScale,
  LinearScale,
  BarElement,
  BarController,
  Tooltip,
  Legend,
} from 'chart.js';

Chart.register(CategoryScale, LinearScale, BarElement, BarController, Tooltip, Legend);

const fmt = (n) => Math.abs(n || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
const fmtDate = (d) => new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
const ACCOUNT_COLORS = ['#7C3AED', '#2563EB', '#059669', '#EA580C', '#DB2777', '#0891B2'];
const CURRENT_YEAR = new Date().getFullYear();
const YEARS = Array.from({ length: 5 }, (_, i) => String(CURRENT_YEAR - i));

const MONTHS_LIST = [
  { v: '', l: 'Toute l\'année' },
  { v: '01', l: 'Janvier' }, { v: '02', l: 'Février' }, { v: '03', l: 'Mars' },
  { v: '04', l: 'Avril' }, { v: '05', l: 'Mai' }, { v: '06', l: 'Juin' },
  { v: '07', l: 'Juillet' }, { v: '08', l: 'Août' }, { v: '09', l: 'Septembre' },
  { v: '10', l: 'Octobre' }, { v: '11', l: 'Novembre' }, { v: '12', l: 'Décembre' },
];

// Last 24 months for comparison pickers
const PERIOD_OPTIONS = Array.from({ length: 24 }, (_, i) => {
  const d = new Date(new Date().getFullYear(), new Date().getMonth() - i, 1);
  const y = d.getFullYear(), m = d.getMonth() + 1;
  return {
    value: `${y}-${String(m).padStart(2, '0')}`,
    label: d.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' }),
  };
});

function periodLabel(ym) {
  const opt = PERIOD_OPTIONS.find(o => o.value === ym);
  return opt ? opt.label : ym;
}

// ── Helpers ───────────────────────────────────────────────────────────────────
function polarToXY(cx, cy, r, angleDeg) {
  const rad = (angleDeg - 90) * Math.PI / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

function donutSlice(cx, cy, r, innerR, startDeg, endDeg) {
  const start = polarToXY(cx, cy, r, startDeg);
  const end = polarToXY(cx, cy, r, endDeg);
  const iStart = polarToXY(cx, cy, innerR, endDeg);
  const iEnd = polarToXY(cx, cy, innerR, startDeg);
  const large = endDeg - startDeg > 180 ? 1 : 0;
  return [`M ${start.x} ${start.y}`, `A ${r} ${r} 0 ${large} 1 ${end.x} ${end.y}`, `L ${iStart.x} ${iStart.y}`, `A ${innerR} ${innerR} 0 ${large} 0 ${iEnd.x} ${iEnd.y}`, 'Z'].join(' ');
}

// ── Donut Chart ───────────────────────────────────────────────────────────────
function DonutChart({ accountId, categories }) {
  const [hovered, setHovered] = useState(null);
  const [localYear, setLocalYear] = useState(String(CURRENT_YEAR));
  const [localMonth, setLocalMonth] = useState(String(new Date().getMonth() + 1).padStart(2, '0'));
  const [transactions, setTransactions] = useState([]);

  useEffect(() => {
    if (!accountId) return;
    let cancelled = false;
    const y = localYear, m = localMonth;
    const params = { accountId };
    if (m) {
      const days = new Date(Number(y), Number(m), 0).getDate();
      params.startDate = `${y}-${m}-01`;
      params.endDate = `${y}-${m}-${days}`;
    } else {
      params.startDate = `${y}-01-01`;
      params.endDate = `${y}-12-31`;
    }
    transactionAPI.getTransactions(params)
      .then(res => { if (!cancelled) setTransactions(res?.data?.data || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [accountId, localYear, localMonth]);

  const expenses = transactions.filter(t => t.type === 'expense');
  const total = expenses.reduce((s, t) => s + Math.abs(t.amount || 0), 0);
  const slices = categories.filter(c => c.type === 'expense').map(cat => {
    const sum = expenses.filter(t => (t.categoryId || t.category) === cat._id).reduce((s, t) => s + Math.abs(t.amount || 0), 0);
    return { ...cat, sum, pct: total > 0 ? sum / total : 0 };
  }).filter(c => c.sum > 0).sort((a, b) => b.sum - a.sum);

  let cursor = 0;
  const arcs = slices.map(s => { const start = cursor; const sweep = s.pct * 360; cursor += sweep; return { ...s, start, end: cursor }; });
  const cx = 130, cy = 130, r = 100, innerR = 62;
  const hov = hovered != null ? slices[hovered] : null;

  const monthLabel = localMonth ? MONTHS_LIST.find(m => m.v === localMonth)?.l : null;
  const periodSub = monthLabel ? `${monthLabel} ${localYear}` : `Année ${localYear}`;

  return (
    <div className="chart-card">
      <div className="chart-card-header">
        <div>
          <h3 className="chart-title">Répartition des dépenses</h3>
          <p className="chart-sub">{periodSub} · Total : {fmt(total)}</p>
        </div>
        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
          <select className="filter-select" value={localYear} onChange={e => setLocalYear(e.target.value)}>
            {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <select className="filter-select" value={localMonth} onChange={e => setLocalMonth(e.target.value)}>
            {MONTHS_LIST.map(m => <option key={m.v} value={m.v}>{m.l}</option>)}
          </select>
        </div>
      </div>
      {slices.length === 0 ? (
        <div className="tx-empty" style={{ padding: 24 }}><p>Aucune dépense sur cette période</p></div>
      ) : (
        <div className="donut-layout">
          <svg width="260" height="260" style={{ flexShrink: 0 }}>
            {arcs.map((arc, i) => {
              const isHov = hovered === i;
              return (
                <path key={arc._id} d={donutSlice(cx, cy, isHov ? r + 6 : r, isHov ? innerR - 2 : innerR, arc.start, arc.end - 0.5)}
                  fill={arc.color || '#7C3AED'} opacity={hovered !== null && !isHov ? 0.35 : 1}
                  style={{ transition: 'all 0.18s', cursor: 'pointer' }}
                  onMouseEnter={() => setHovered(i)} onMouseLeave={() => setHovered(null)} />
              );
            })}
            {hov ? (
              <>
                <text x={cx} y={cy + 12} textAnchor="middle" fill="var(--text)" fontSize="13" fontWeight="700" fontFamily="Inter">{fmt(hov.sum)}</text>
                <text x={cx} y={cy + 28} textAnchor="middle" fill="var(--text2)" fontSize="11" fontFamily="Inter">{(hov.pct * 100).toFixed(1)}%</text>
              </>
            ) : (
              <>
                <text x={cx} y={cy - 6} textAnchor="middle" fill="var(--text2)" fontSize="11" fontFamily="Inter" fontWeight="600">TOTAL</text>
                <text x={cx} y={cy + 16} textAnchor="middle" fill="var(--text)" fontSize="20" fontWeight="700" fontFamily="Inter">{fmt(total)}</text>
              </>
            )}
          </svg>
          <div className="donut-legend">
            {slices.map((s, i) => (
              <div key={s._id} className={`donut-legend-item${hovered === i ? ' active' : ''}`}
                onMouseEnter={() => setHovered(i)} onMouseLeave={() => setHovered(null)}>
                <span className="dl-dot" style={{ background: s.color || '#7C3AED' }} />
                <span className="dl-name">{s.name}</span>
                <span className="dl-pct">{(s.pct * 100).toFixed(0)}%</span>
                <span className="dl-amt">{fmt(s.sum)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Chart Canvas (shared) ─────────────────────────────────────────────────────
function ChartCanvas({ setup, deps }) {
  const canvasRef = useRef(null);
  const chartRef  = useRef(null);
  const depsKey   = JSON.stringify(deps);

  useEffect(() => {
    if (!canvasRef.current) return;
    if (chartRef.current) { chartRef.current.destroy(); chartRef.current = null; }
    const t = setTimeout(() => {
      if (canvasRef.current) chartRef.current = setup(canvasRef.current);
    }, 50);
    return () => {
      clearTimeout(t);
      if (chartRef.current) { chartRef.current.destroy(); chartRef.current = null; }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [depsKey, setup]);

  return <canvas ref={canvasRef} style={{ width: '100%', height: '100%' }} />;
}

// ── Bar Chart ─────────────────────────────────────────────────────────────────
const MONTHS_SHORT_FR = ['Jan','Fév','Mar','Avr','Mai','Juin','Juil','Août','Sep','Oct','Nov','Déc'];

function BarChart({ accountId }) {
  const [localYear, setLocalYear] = useState(String(CURRENT_YEAR));
  const [transactions, setTransactions] = useState([]);

  useEffect(() => {
    if (!accountId) return;
    let cancelled = false;
    transactionAPI.getTransactions({
      accountId,
      startDate: `${localYear}-01-01`,
      endDate: `${localYear}-12-31`,
    }).then(res => { if (!cancelled) setTransactions(res?.data?.data || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [accountId, localYear]);

  const monthlyData = Array.from({ length: 12 }, (_, i) => {
    const m = i + 1;
    const key = `${localYear}-${String(m).padStart(2, '0')}`;
    const txs = transactions.filter(t => t.date?.startsWith(key));
    const income  = parseFloat(txs.filter(t => t.type === 'income') .reduce((s, t) => s + Math.abs(t.amount || 0), 0).toFixed(2));
    const expense = parseFloat(txs.filter(t => t.type === 'expense').reduce((s, t) => s + Math.abs(t.amount || 0), 0).toFixed(2));
    return { label: MONTHS_SHORT_FR[i], income, expense, net: parseFloat((income - expense).toFixed(2)) };
  });

  const gridColor = 'rgba(255,255,255,0.05)';
  const tickColor = 'rgba(255,255,255,0.35)';
  const fmtY = v => `${Math.abs(v) >= 1000 ? (v / 1000).toFixed(1) + 'k' : v}€`;
  const tooltipOpts = { backgroundColor: '#181b2e', borderColor: 'rgba(255,255,255,0.1)', borderWidth: 1, titleColor: '#f0f0f8', bodyColor: '#f0f0f8' };
  const axisOpts = { x: { grid: { color: gridColor }, ticks: { color: tickColor, font: { size: 11 } } }, y: { grid: { color: gridColor }, ticks: { color: tickColor, font: { size: 11 }, callback: fmtY } } };

  const setupMonths = useCallback(canvas => {
    return new Chart(canvas, {
      type: 'bar',
      data: {
        labels: monthlyData.map(d => d.label),
        datasets: [
          { label: 'Revenus',  data: monthlyData.map(d => d.income),  backgroundColor: 'rgba(34,197,94,0.60)',  borderRadius: 4, borderSkipped: false },
          { label: 'Dépenses', data: monthlyData.map(d => d.expense), backgroundColor: 'rgba(239,68,68,0.60)',  borderRadius: 4, borderSkipped: false },
          { label: 'Net',      data: monthlyData.map(d => d.net),     backgroundColor: 'rgba(99,102,241,0.65)', borderRadius: 4, borderSkipped: false },
        ],
      },
      options: {
        responsive: true, maintainAspectRatio: false,
        plugins: {
          legend: { display: true, labels: { color: tickColor, font: { size: 11 }, boxWidth: 12, boxHeight: 12 } },
          tooltip: { ...tooltipOpts, callbacks: { label: ctx => `${ctx.dataset.label} : ${ctx.parsed.y >= 0 ? '+' : ''}${ctx.parsed.y.toFixed(2)} €` } },
        },
        scales: axisOpts,
      },
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monthlyData]);

  const setupTotal = useCallback(canvas => {
    const totalIncome  = parseFloat(monthlyData.reduce((s, m) => s + m.income,  0).toFixed(2));
    const totalExpense = parseFloat(monthlyData.reduce((s, m) => s + m.expense, 0).toFixed(2));
    const totalNet     = parseFloat((totalIncome - totalExpense).toFixed(2));
    const TC = 'rgba(251,191,36,';
    return new Chart(canvas, {
      type: 'bar',
      data: {
        labels: ['Total'],
        datasets: [
          { label: 'Revenus',  data: [totalIncome],  backgroundColor: `${TC}0.70)`, borderRadius: 4, borderSkipped: false },
          { label: 'Dépenses', data: [totalExpense], backgroundColor: `${TC}0.85)`, borderRadius: 4, borderSkipped: false },
          { label: 'Net',      data: [totalNet],     backgroundColor: `${TC}1.0)`,  borderRadius: 4, borderSkipped: false },
        ],
      },
      options: {
        responsive: true, maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: { ...tooltipOpts, callbacks: { label: ctx => `${ctx.dataset.label} : ${ctx.parsed.y >= 0 ? '+' : ''}${ctx.parsed.y.toFixed(2)} €` } },
        },
        scales: axisOpts,
      },
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monthlyData]);

  return (
    <div className="chart-card">
      <div className="chart-card-header">
        <div>
          <h3 className="chart-title">Revenus vs Dépenses</h3>
          <p className="chart-sub">Mois par mois · {localYear}</p>
        </div>
        <select className="filter-select" value={localYear} onChange={e => setLocalYear(e.target.value)}>
          {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>
      <div style={{ display: 'flex', height: 280 }}>
        <div style={{ flex: 3, minWidth: 0 }}>
          <ChartCanvas setup={setupMonths} deps={[monthlyData]} />
        </div>
        <div style={{ width: 1, background: 'rgba(255,255,255,0.08)', margin: '8px 6px' }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <ChartCanvas setup={setupTotal} deps={[monthlyData]} />
        </div>
      </div>
    </div>
  );
}

// ── Line Chart ────────────────────────────────────────────────────────────────
function LineChart({ transactions, account }) {
  const [hovPt, setHovPt] = useState(null);
  const txs = [...transactions].sort((a, b) => new Date(a.date) - new Date(b.date));
  const reversedTotal = txs.reduce((s, t) => s + (t.type === 'income' ? 1 : -1) * Math.abs(t.amount || 0), 0);
  const startBal = (account?.balance || 0) - reversedTotal;
  let bal = startBal;
  const points = txs.map(t => { bal += (t.type === 'income' ? 1 : -1) * Math.abs(t.amount || 0); return { date: t.date, balance: bal }; });
  const allPoints = txs.length > 0 ? [{ date: txs[0].date, balance: startBal }, ...points] : [{ date: new Date().toISOString().split('T')[0], balance: account?.balance || 0 }];

  const W = 560, H = 200, pad = { t: 20, r: 20, b: 40, l: 60 };
  const chartW = W - pad.l - pad.r, chartH = H - pad.t - pad.b;
  const balances = allPoints.map(p => p.balance);
  const minBal = Math.min(...balances), maxBal = Math.max(...balances);
  const range = Math.max(maxBal - minBal, 1);
  const toXY = (i) => ({
    x: pad.l + (i / Math.max(allPoints.length - 1, 1)) * chartW,
    y: pad.t + chartH - ((allPoints[i].balance - minBal) / range) * chartH
  });
  const linePoints = allPoints.map((_, i) => toXY(i));
  const pathD = linePoints.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
  const areaD = pathD + ` L ${linePoints[linePoints.length - 1].x} ${pad.t + chartH} L ${pad.l} ${pad.t + chartH} Z`;

  return (
    <div className="chart-card">
      <div className="chart-card-header">
        <div>
          <h3 className="chart-title">Évolution du solde</h3>
          <p className="chart-sub">{account?.label || 'Compte courant'}</p>
        </div>
      </div>
      {txs.length === 0 ? (
        <div className="tx-empty" style={{ padding: 24 }}><p>Aucune transaction sur cette période</p></div>
      ) : (
        <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ overflow: 'visible' }}>
          <defs>
            <linearGradient id="lineGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="var(--accent)" stopOpacity="0.3" />
              <stop offset="100%" stopColor="var(--accent)" stopOpacity="0.02" />
            </linearGradient>
          </defs>
          {Array.from({ length: 5 }, (_, i) => {
            const v = minBal + (range / 4) * i;
            const y = pad.t + chartH - (i / 4) * chartH;
            return (
              <g key={i}>
                <line x1={pad.l} y1={y} x2={W - pad.r} y2={y} stroke="var(--border)" strokeWidth="1" />
                <text x={pad.l - 8} y={y + 4} textAnchor="end" fill="var(--text3)" fontSize="10" fontFamily="Inter">
                  {v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v.toFixed(0)}
                </text>
              </g>
            );
          })}
          <path d={areaD} fill="url(#lineGrad)" />
          <path d={pathD} fill="none" stroke="var(--accent)" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
          {linePoints.map((p, i) => (
            <circle key={i} cx={p.x} cy={p.y} r={hovPt === i ? 5 : 3}
              fill={hovPt === i ? 'var(--accent)' : 'var(--bg2)'} stroke="var(--accent)" strokeWidth="2"
              style={{ cursor: 'pointer', transition: 'r 0.1s' }}
              onMouseEnter={() => setHovPt(i)} onMouseLeave={() => setHovPt(null)} />
          ))}
          {hovPt !== null && (() => {
            const p = linePoints[hovPt];
            const pt = allPoints[hovPt];
            const x = Math.min(Math.max(p.x, 60), W - 80);
            return (
              <g>
                <rect x={x - 44} y={p.y - 44} width={88} height={36} rx="6" fill="var(--bg3)" stroke="var(--border2)" />
                <text x={x} y={p.y - 26} textAnchor="middle" fill="var(--text)" fontSize="11" fontFamily="Inter" fontWeight="700">{fmt(pt.balance)}</text>
                <text x={x} y={p.y - 14} textAnchor="middle" fill="var(--text3)" fontSize="10" fontFamily="Inter">{fmtDate(pt.date)}</text>
              </g>
            );
          })()}
          <line x1={pad.l} y1={pad.t + chartH} x2={W - pad.r} y2={pad.t + chartH} stroke="var(--border2)" strokeWidth="1" />
        </svg>
      )}
    </div>
  );
}

// ── Heatmap ───────────────────────────────────────────────────────────────────
function HeatmapChart({ transactions, year, month }) {
  const [hovDay, setHovDay] = useState(null);
  const displayYear = Number(year), displayMonth = Number(month) || new Date().getMonth() + 1;
  const daysInMonth = new Date(displayYear, displayMonth, 0).getDate();
  const firstDay = new Date(displayYear, displayMonth - 1, 1).getDay();
  const startOffset = (firstDay + 6) % 7;
  const monthKey = `${year}-${String(displayMonth).padStart(2, '0')}`;

  const expByDay = {};
  transactions.filter(t => t.type === 'expense' && t.date?.startsWith(monthKey)).forEach(t => {
    const day = parseInt(t.date.split('-')[2]);
    expByDay[day] = (expByDay[day] || 0) + Math.abs(t.amount || 0);
  });

  const maxExp = Math.max(...Object.values(expByDay), 1);
  const days = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];
  const cellSize = 36, gap = 5;
  const cells = [];
  for (let d = 1; d <= daysInMonth; d++) {
    const idx = d - 1 + startOffset;
    cells.push({ d, x: Math.floor(idx / 7) * (cellSize + gap), y: (idx % 7) * (cellSize + gap), exp: expByDay[d] || 0, intensity: (expByDay[d] || 0) / maxExp });
  }
  const totalCols = Math.ceil((daysInMonth + startOffset) / 7);
  const totalW = totalCols * (cellSize + gap) - gap;
  const totalH = 7 * (cellSize + gap) - gap;
  const monthName = new Date(displayYear, displayMonth - 1, 1).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });

  return (
    <div className="chart-card">
      <div className="chart-card-header">
        <div>
          <h3 className="chart-title">Heatmap des dépenses — {monthName}</h3>
          <p className="chart-sub">Intensité journalière des dépenses</p>
        </div>
        <div className="heatmap-scale">
          <span style={{ fontSize: 10, color: 'var(--text3)' }}>Faible</span>
          {[0.15, 0.35, 0.55, 0.75, 1].map((v, i) => (
            <div key={i} style={{ width: 16, height: 16, borderRadius: 4, background: `oklch(${45 + v * 20}% 0.19 278 / ${0.1 + v * 0.9})` }} />
          ))}
          <span style={{ fontSize: 10, color: 'var(--text3)' }}>Élevée</span>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap, paddingTop: 2 }}>
          {days.map((d, i) => (
            <div key={i} style={{ height: cellSize, display: 'flex', alignItems: 'center', fontSize: 11, color: 'var(--text3)', fontWeight: 600, width: 14 }}>{d}</div>
          ))}
        </div>
        <svg width={totalW} height={totalH} style={{ overflow: 'visible' }}>
          {cells.map(cell => {
            const isHov = hovDay === cell.d;
            const hasExp = cell.exp > 0;
            return (
              <g key={cell.d} onMouseEnter={() => setHovDay(cell.d)} onMouseLeave={() => setHovDay(null)} style={{ cursor: hasExp ? 'pointer' : 'default' }}>
                <rect x={cell.x} y={cell.y} width={cellSize} height={cellSize} rx="6"
                  fill={hasExp ? `oklch(${48 + cell.intensity * 18}% 0.19 278 / ${0.15 + cell.intensity * 0.85})` : 'var(--bg3)'}
                  stroke={isHov ? 'var(--accent)' : 'transparent'} strokeWidth="1.5"
                  style={{ transition: 'stroke 0.12s' }} />
                <text x={cell.x + cellSize / 2} y={cell.y + cellSize / 2 + 1} textAnchor="middle" dominantBaseline="middle"
                  fill={hasExp && cell.intensity > 0.5 ? 'white' : 'var(--text2)'}
                  fontSize="11" fontFamily="Inter" fontWeight={hasExp ? '600' : '400'}>{cell.d}</text>
                {isHov && hasExp && (
                  <g>
                    <rect x={cell.x - 20} y={cell.y - 38} width={cellSize + 40} height={28} rx="6" fill="var(--bg3)" stroke="var(--border2)" />
                    <text x={cell.x + cellSize / 2} y={cell.y - 24} textAnchor="middle" fill="var(--red)" fontSize="11" fontFamily="Inter" fontWeight="700">−{fmt(cell.exp)}</text>
                  </g>
                )}
              </g>
            );
          })}
        </svg>
      </div>
      {Object.keys(expByDay).length > 0 && (
        <div style={{ marginTop: 12, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          {Object.entries(expByDay).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([day, amt]) => (
            <div key={day} style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 12 }}>
              <span style={{ color: 'var(--text3)' }}>{String(displayMonth).padStart(2, '0')}/{day.padStart(2, '0')}</span>
              <span style={{ color: 'var(--red)', fontWeight: 650 }}>−{fmt(amt)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Date Range Comparison Chart ───────────────────────────────────────────────
function DateRangeComparisonChart({ accountId, categories }) {
  const now = new Date();
  const y = now.getFullYear(), m = now.getMonth() + 1;
  const prevM = m === 1 ? 12 : m - 1;
  const prevY = m === 1 ? y - 1 : y;
  const lastDayPrev = new Date(prevY, prevM, 0).getDate();

  const [startA, setStartA] = useState(`${prevY}-${String(prevM).padStart(2, '0')}-01`);
  const [endA, setEndA] = useState(`${prevY}-${String(prevM).padStart(2, '0')}-${lastDayPrev}`);
  const [startB, setStartB] = useState(`${y}-${String(m).padStart(2, '0')}-01`);
  const [endB, setEndB] = useState(now.toISOString().split('T')[0]);
  const [dataA, setDataA] = useState([]);
  const [dataB, setDataB] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hovA, setHovA] = useState(null);
  const [hovB, setHovB] = useState(null);
  const fetchRef = useRef(0);

  useEffect(() => {
    if (!accountId || !startA || !endA || !startB || !endB) return;
    const id = ++fetchRef.current;
    setLoading(true);
    Promise.all([
      transactionAPI.getTransactions({ accountId, startDate: startA, endDate: endA }),
      transactionAPI.getTransactions({ accountId, startDate: startB, endDate: endB }),
    ]).then(([resA, resB]) => {
      if (fetchRef.current !== id) return;
      setDataA(resA?.data?.data || []);
      setDataB(resB?.data?.data || []);
    }).finally(() => { if (fetchRef.current === id) setLoading(false); });
  }, [accountId, startA, endA, startB, endB]);

  const expCats = categories.filter(c => c.type === 'expense');

  const processSlices = (data) => {
    const expenses = data.filter(t => t.type === 'expense');
    const total = expenses.reduce((s, t) => s + Math.abs(t.amount || 0), 0);
    const slices = expCats.map(cat => {
      const sum = expenses.filter(t => (t.categoryId || t.category) === cat._id)
        .reduce((s, t) => s + Math.abs(t.amount || 0), 0);
      return { ...cat, sum, pct: total > 0 ? sum / total : 0 };
    }).filter(c => c.sum > 0).sort((a, b) => b.sum - a.sum);
    return { slices, total };
  };

  const { slices: slicesA, total: totalA } = processSlices(dataA);
  const { slices: slicesB, total: totalB } = processSlices(dataB);

  const buildArcs = (slices) => {
    let cursor = 0;
    return slices.map(s => { const start = cursor; const sw = s.pct * 360; cursor += sw; return { ...s, start, end: cursor }; });
  };

  const arcsA = buildArcs(slicesA);
  const arcsB = buildArcs(slicesB);

  const sumMapA = Object.fromEntries(slicesA.map(s => [s._id, s.sum]));
  const sumMapB = Object.fromEntries(slicesB.map(s => [s._id, s.sum]));
  const allCatIds = [...new Set([...slicesA.map(s => s._id), ...slicesB.map(s => s._id)])];
  const catRef = Object.fromEntries([...slicesA, ...slicesB].map(s => [s._id, s]));

  const compRows = allCatIds.map(id => {
    const cat = catRef[id];
    const sA = sumMapA[id] || 0;
    const sB = sumMapB[id] || 0;
    const pctA = slicesA.find(s => s._id === id)?.pct || 0;
    const pctB = slicesB.find(s => s._id === id)?.pct || 0;
    const delta = sA > 0 ? (sB - sA) / sA * 100 : null;
    return { cat, sA, sB, pctA, pctB, delta };
  }).sort((a, b) => (b.sA + b.sB) - (a.sA + a.sB));

  const cx = 90, cy = 90, r = 72, innerR = 44;

  const renderDonut = (arcs, total, hov, setHov, label, isB) => (
    <div className="drcomp-section">
      <div className="drcomp-section-label">{label}</div>
      <svg width="180" height="180" style={{ flexShrink: 0 }}>
        {arcs.length > 0 ? arcs.map((arc, i) => (
          <path key={arc._id}
            d={donutSlice(cx, cy, hov === i ? r + 5 : r, hov === i ? innerR - 2 : innerR, arc.start, arc.end - 0.5)}
            fill={arc.color || '#7C3AED'}
            opacity={hov !== null && hov !== i ? 0.28 : 1}
            style={{ transition: 'all 0.18s', cursor: 'pointer' }}
            onMouseEnter={() => setHov(i)} onMouseLeave={() => setHov(null)}
          />
        )) : (
          <circle cx={cx} cy={cy} r={r} fill="var(--bg3)" />
        )}
        {arcs.length > 0 ? (
          hov !== null ? (
            <>
              <text x={cx} y={cy + 8} textAnchor="middle" fill="var(--text)" fontSize="12" fontWeight="700" fontFamily="Inter">{fmt(arcs[hov].sum)}</text>
              <text x={cx} y={cy + 22} textAnchor="middle" fill="var(--text2)" fontSize="10" fontFamily="Inter">{(arcs[hov].pct * 100).toFixed(1)}%</text>
            </>
          ) : (
            <>
              <text x={cx} y={cy - 4} textAnchor="middle" fill="var(--text2)" fontSize="10" fontFamily="Inter" fontWeight="600">TOTAL</text>
              <text x={cx} y={cy + 14} textAnchor="middle" fill="var(--text)" fontSize="16" fontWeight="700" fontFamily="Inter">{fmt(total)}</text>
            </>
          )
        ) : (
          <text x={cx} y={cy + 5} textAnchor="middle" fill="var(--text3)" fontSize="11" fontFamily="Inter">Aucune dépense</text>
        )}
      </svg>
      <div className="drcomp-legend-list">
        {(isB ? compRows : slicesA.map(s => ({ cat: s, sA: s.sum, sB: sumMapB[s._id] || 0, pctA: s.pct, pctB: 0, delta: null }))).map((row, i) => {
          const isHov = isB ? hov === arcsB.findIndex(a => a._id === row.cat._id) : hov === i;
          const sVal = isB ? row.sB : row.sA;
          const pctVal = isB ? row.pctB : row.pctA;
          if (sVal === 0 && !isB) return null;
          const delta = isB ? row.delta : null;
          const isGood = delta !== null && delta < 0;
          const isBad = delta !== null && delta > 0;
          const isNew = delta === null && isB && row.sB > 0 && row.sA === 0;
          return (
            <div key={row.cat._id}
              className={`drcomp-legend-row${isHov ? ' hov' : ''}`}
              onMouseEnter={() => {
                const idx = isB ? arcsB.findIndex(a => a._id === row.cat._id) : i;
                setHov(idx >= 0 ? idx : null);
              }}
              onMouseLeave={() => setHov(null)}
            >
              <span className="drcomp-dot" style={{ background: row.cat.color || '#7C3AED' }} />
              <span className="drcomp-cat-name">{row.cat.name}</span>
              <span className="drcomp-pct">{pctVal > 0 ? `${(pctVal * 100).toFixed(0)}%` : '—'}</span>
              {isB && (
                <span className={`drcomp-delta${isGood ? ' good' : isBad ? ' bad' : ''}`}>
                  {isNew ? 'Nouveau' : delta === null ? '—' : delta === 0 ? '=' : `${delta > 0 ? '+' : ''}${delta.toFixed(0)}%`}
                </span>
              )}
            </div>
          );
        }).filter(Boolean)}
      </div>
    </div>
  );

  const fmtDateShort = (s) => s ? new Date(s).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) : '';

  return (
    <div className="chart-card full-width">
      <div className="chart-card-header">
        <div>
          <h3 className="chart-title">Comparaison par plages de dates</h3>
          <p className="chart-sub">Dépenses par catégorie — vert si inférieur à la période A, rouge si supérieur</p>
        </div>
      </div>

      <div className="drcomp-pickers">
        <div className="drcomp-picker-group">
          <span className="drcomp-picker-label ref">Période A · référence</span>
          <div className="drcomp-date-row">
            <input type="date" className="drcomp-date-input" value={startA} onChange={e => setStartA(e.target.value)} />
            <span className="drcomp-arrow">→</span>
            <input type="date" className="drcomp-date-input" value={endA} onChange={e => setEndA(e.target.value)} />
          </div>
          {startA && endA && <span className="drcomp-picker-sub">{fmtDateShort(startA)} – {fmtDateShort(endA)}</span>}
        </div>
        <div className="drcomp-vs-pill">VS</div>
        <div className="drcomp-picker-group">
          <span className="drcomp-picker-label">Période B · comparée</span>
          <div className="drcomp-date-row">
            <input type="date" className="drcomp-date-input" value={startB} onChange={e => setStartB(e.target.value)} />
            <span className="drcomp-arrow">→</span>
            <input type="date" className="drcomp-date-input" value={endB} onChange={e => setEndB(e.target.value)} />
          </div>
          {startB && endB && <span className="drcomp-picker-sub">{fmtDateShort(startB)} – {fmtDateShort(endB)}</span>}
        </div>
      </div>

      {loading ? (
        <div style={{ padding: 32, textAlign: 'center' }}><div className="loading-spinner" style={{ margin: '0 auto' }} /></div>
      ) : slicesA.length === 0 && slicesB.length === 0 ? (
        <div className="comp-empty">Aucune dépense sur ces deux périodes.</div>
      ) : (
        <div className="drcomp-body">
          {renderDonut(arcsA, totalA, hovA, setHovA, 'Période A', false)}
          <div className="drcomp-body-divider" />
          {renderDonut(arcsB, totalB, hovB, setHovB, 'Période B', true)}
        </div>
      )}
    </div>
  );
}

// ── Comparison Chart ──────────────────────────────────────────────────────────
function ComparisonChart({ accountId, categories }) {
  const now = new Date();
  const curY = now.getFullYear(), curM = now.getMonth() + 1;
  const prevM = curM === 1 ? 12 : curM - 1;
  const prevY = curM === 1 ? curY - 1 : curY;
  const [periodA, setPeriodA] = useState(`${prevY}-${String(prevM).padStart(2, '0')}`);
  const [periodB, setPeriodB] = useState(`${curY}-${String(curM).padStart(2, '0')}`);
  const [dataA, setDataA] = useState([]);
  const [dataB, setDataB] = useState([]);
  const [loading, setLoading] = useState(false);
  const fetchRef = useRef(0);

  const fetchPeriod = useCallback(async (ym) => {
    const [y, m] = ym.split('-').map(Number);
    const daysInMonth = new Date(y, m, 0).getDate();
    const startDate = `${y}-${String(m).padStart(2, '0')}-01`;
    const endDate = `${y}-${String(m).padStart(2, '0')}-${daysInMonth}`;
    const res = await transactionAPI.getTransactions({ accountId, startDate, endDate });
    return res?.data?.data || [];
  }, [accountId]);

  useEffect(() => {
    if (!accountId) return;
    const id = ++fetchRef.current;
    setLoading(true);
    Promise.all([fetchPeriod(periodA), fetchPeriod(periodB)]).then(([a, b]) => {
      if (fetchRef.current !== id) return;
      setDataA(a); setDataB(b);
    }).finally(() => { if (fetchRef.current === id) setLoading(false); });
  }, [accountId, periodA, periodB, fetchPeriod]);

  const catMap = Object.fromEntries(categories.map(c => [c._id, c]));
  const allCatIds = [...new Set([...dataA.map(t => t.categoryId), ...dataB.map(t => t.categoryId)])].filter(id => catMap[id]);

  const rows = allCatIds.map(catId => {
    const cat = catMap[catId];
    const sumA = dataA.filter(t => t.categoryId === catId).reduce((s, t) => s + Math.abs(t.amount || 0), 0);
    const sumB = dataB.filter(t => t.categoryId === catId).reduce((s, t) => s + Math.abs(t.amount || 0), 0);
    const delta = sumB - sumA;
    return { cat, sumA, sumB, delta };
  }).sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta));

  const expenseRows = rows.filter(r => r.cat.type === 'expense');
  const incomeRows = rows.filter(r => r.cat.type === 'income');
  const maxAmt = Math.max(...rows.flatMap(r => [r.sumA, r.sumB]), 1);

  const deltaClass = (row) => {
    if (row.delta === 0) return 'neutral';
    if (row.cat.type === 'expense') return row.delta > 0 ? 'bad' : 'good';
    return row.delta > 0 ? 'good' : 'bad';
  };

  const tagA = periodLabel(periodA).split(' ')[0];
  const tagB = periodLabel(periodB).split(' ')[0];

  const renderRows = (list) => list.map(row => (
    <div className="comp-row" key={row.cat._id}>
      <div className="comp-cat-info">
        <span className="cat-dot" style={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0, background: row.cat.color || '#7C3AED' }} />
        <span className="comp-cat-name">{row.cat.name}</span>
      </div>
      <div className="comp-bars-col">
        <div className="comp-bar-row">
          <span className="comp-period-tag">{tagA}</span>
          <div className="comp-bar-track">
            <div className="comp-bar-fill" style={{ width: `${(row.sumA / maxAmt) * 100}%`, background: row.cat.color || '#7C3AED', opacity: 0.45 }} />
          </div>
          <span className="comp-amt">{fmt(row.sumA)}</span>
          <span style={{ width: 68, flexShrink: 0 }} />
        </div>
        <div className="comp-bar-row">
          <span className="comp-period-tag">{tagB}</span>
          <div className="comp-bar-track">
            <div className="comp-bar-fill" style={{ width: `${(row.sumB / maxAmt) * 100}%`, background: row.cat.color || '#7C3AED' }} />
          </div>
          <span className="comp-amt">{fmt(row.sumB)}</span>
          <span className={`comp-delta ${deltaClass(row)}`}>
            {row.delta === 0 ? '—' : `${row.delta > 0 ? '+' : ''}${fmt(row.delta)}`}
          </span>
        </div>
      </div>
    </div>
  ));

  return (
    <div className="chart-card full-width">
      <div className="chart-card-header">
        <div>
          <h3 className="chart-title">Comparaison de périodes</h3>
          <p className="chart-sub">Évolution par catégorie entre deux mois</p>
        </div>
      </div>

      <div className="comp-pickers">
        <span className="period-bar-label">Période A</span>
        <select className="filter-select" value={periodA} onChange={e => setPeriodA(e.target.value)}>
          {PERIOD_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <span className="comp-vs">VS</span>
        <span className="period-bar-label">Période B</span>
        <select className="filter-select" value={periodB} onChange={e => setPeriodB(e.target.value)}>
          {PERIOD_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>

      {loading ? (
        <div style={{ padding: 24, textAlign: 'center' }}><div className="loading-spinner" style={{ margin: '0 auto' }} /></div>
      ) : rows.length === 0 ? (
        <div className="comp-empty">Aucune transaction sur ces deux périodes.</div>
      ) : (
        <>
          {incomeRows.length > 0 && (
            <>
              <div className="comp-group-title">Revenus</div>
              {renderRows(incomeRows)}
            </>
          )}
          {expenseRows.length > 0 && (
            <>
              <div className="comp-group-title">Dépenses</div>
              {renderRows(expenseRows)}
            </>
          )}
        </>
      )}
    </div>
  );
}

// ── Icons ─────────────────────────────────────────────────────────────────────
const IconDonut    = () => <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="4"/></svg>;
const IconBar      = () => <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="3" y="10" width="4" height="11" rx="1"/><rect x="10" y="6" width="4" height="15" rx="1"/><rect x="17" y="3" width="4" height="18" rx="1"/></svg>;
const IconLine     = () => <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><polyline points="3 17 9 11 13 15 21 7"/></svg>;
const IconGrid     = () => <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>;
const IconCalendar = () => <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>;
const IconArrows   = () => <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M8 7l-5 5 5 5"/><path d="M16 7l5 5-5 5"/><line x1="3" y1="12" x2="21" y2="12"/></svg>;

// ── Mini Previews ─────────────────────────────────────────────────────────────
function MiniDonut() {
  const segs = [{ pct: 0.35, c: '#EC4899' }, { pct: 0.25, c: '#8B5CF6' }, { pct: 0.22, c: '#F59E0B' }, { pct: 0.18, c: '#6366F1' }];
  let cur = 0;
  return (
    <svg width="60" height="60" viewBox="0 0 60 60" style={{ flexShrink: 0 }}>
      {segs.map((s, i) => { const st = cur * 360; cur += s.pct; return <path key={i} d={donutSlice(30, 30, 22, 13, st, cur * 360 - 1)} fill={s.c} opacity={0.85} />; })}
    </svg>
  );
}

function MiniBar() {
  const bars = [4, 7, 5, 9, 6, 8, 3, 10, 7, 5, 8, 6];
  return (
    <svg width="100%" height="44" viewBox="0 0 108 44" preserveAspectRatio="none">
      {bars.map((v, i) => <rect key={i} x={i * 9 + 1} y={44 - v * 4} width="7" height={v * 4} rx="2" fill="rgba(245,158,11,0.65)" />)}
    </svg>
  );
}

function MiniLine() {
  const pts = [30, 35, 28, 40, 38, 45, 42, 50, 47, 55, 50, 58];
  const minV = Math.min(...pts), maxV = Math.max(...pts), range = maxV - minV || 1;
  const coords = pts.map((v, i) => ({ x: (i / (pts.length - 1)) * 120, y: 40 - ((v - minV) / range) * 37 - 3 }));
  const d = coords.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
  return (
    <svg width="100%" height="44" viewBox="0 0 120 40" preserveAspectRatio="none">
      <defs><linearGradient id="mLG" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#6366F1" stopOpacity="0.3"/><stop offset="100%" stopColor="#6366F1" stopOpacity="0"/></linearGradient></defs>
      <path d={`${d} L 120 40 L 0 40 Z`} fill="url(#mLG)" />
      <path d={d} fill="none" stroke="#6366F1" strokeWidth="2" strokeLinejoin="round" />
    </svg>
  );
}

function MiniHeatmap() {
  const vals = [0.1,0.8,0.3,0.05,0.9, 0.4,0.6,0.2,0.7,0.15, 0.5,0.3,0.85,0.4,0.1, 0.9,0.2,0.6,0.35,0.75, 0.1,0.5,0.8,0.3,0.6];
  return (
    <svg width="64" height="64" viewBox="0 0 60 60" style={{ flexShrink: 0 }}>
      {vals.map((v, i) => <rect key={i} x={(i % 5) * 12} y={Math.floor(i / 5) * 12} width="10" height="10" rx="2" fill={`oklch(${45 + v * 20}% 0.19 278 / ${0.1 + v * 0.9})`} />)}
    </svg>
  );
}

function MiniCompare() {
  const draw = (segs, cx, cy) => { let cur = 0; return segs.map((s, i) => { const st = cur * 360; cur += s.pct; return <path key={i} d={donutSlice(cx, cy, 16, 9, st, cur * 360 - 1)} fill={s.c} opacity={0.85} />; }); };
  return (
    <svg width="80" height="40" viewBox="0 0 80 40" style={{ flexShrink: 0 }}>
      {draw([{ pct: 0.4, c: '#10B981' }, { pct: 0.35, c: '#6366F1' }, { pct: 0.25, c: '#F59E0B' }], 18, 20)}
      {draw([{ pct: 0.3, c: '#10B981' }, { pct: 0.45, c: '#6366F1' }, { pct: 0.25, c: '#F59E0B' }], 62, 20)}
      <text x="40" y="24" textAnchor="middle" fill="rgba(255,255,255,0.25)" fontSize="8" fontFamily="Inter">VS</text>
    </svg>
  );
}

function MiniCompareBar() {
  const pairs = [[6,4],[8,9],[5,7],[3,5],[9,6]];
  return (
    <svg width="100%" height="44" viewBox="0 0 65 44" preserveAspectRatio="none">
      {pairs.map(([a, b], i) => (
        <g key={i}>
          <rect x={i*13}   y={44-a*4} width="5" height={a*4} rx="1" fill="rgba(59,130,246,0.5)" />
          <rect x={i*13+6} y={44-b*4} width="5" height={b*4} rx="1" fill="rgba(59,130,246,0.85)" />
        </g>
      ))}
    </svg>
  );
}

// ── useIsDesktop ──────────────────────────────────────────────────────────────
function useIsDesktop() {
  const [ok, setOk] = useState(() => window.matchMedia('(min-width: 768px)').matches);
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)');
    const h = e => setOk(e.matches);
    mq.addEventListener('change', h);
    return () => mq.removeEventListener('change', h);
  }, []);
  return ok;
}

// ── ChartCard ─────────────────────────────────────────────────────────────────
function ChartCard({ id, title, subtitle, accentColor, icon, preview, onOpen }) {
  const ref = useRef(null);
  const open = () => onOpen(id, ref.current);
  return (
    <div ref={ref} className="cct-card"
      role="button" tabIndex={0}
      aria-label={`Ouvrir le graphique ${title}`}
      onClick={open}
      onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), open())}>
      <div className="cct-top">
        <div className="cct-icon-wrap" style={{ background: `${accentColor}28`, color: accentColor }}>{icon}</div>
        <div className="cct-texts">
          <div className="cct-title">{title}</div>
          <div className="cct-sub">{subtitle}</div>
        </div>
      </div>
      <div className="cct-preview">{preview}</div>
    </div>
  );
}

// ── ChartModal ────────────────────────────────────────────────────────────────
function ChartModal({ title, cardEl, onClose, children }) {
  const overlayRef = useRef(null);
  const panelRef   = useRef(null);
  const closeRef   = useRef(null);
  const closingRef = useRef(false);
  const reduced    = useRef(typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    if (cardEl) cardEl.style.visibility = 'hidden';
    closeRef.current?.focus();

    if (!reduced.current && panelRef.current && cardEl) {
      const first = cardEl.getBoundingClientRect();
      const last  = panelRef.current.getBoundingClientRect();
      const dx = first.left - last.left, dy = first.top - last.top;
      const sx = first.width / last.width, sy = first.height / last.height;
      panelRef.current.animate([
        { transform: `translate(${dx}px,${dy}px) scale(${sx},${sy})`, opacity: 0.6, borderRadius: '16px' },
        { transform: 'translate(0,0) scale(1,1)',                      opacity: 1,   borderRadius: '20px' },
      ], { duration: 380, easing: 'cubic-bezier(0.32,0.72,0,1)', fill: 'forwards' });
      overlayRef.current?.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 200, easing: 'ease', fill: 'forwards' });
    }

    return () => { document.body.style.overflow = ''; if (cardEl) cardEl.style.visibility = ''; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const doClose = useCallback(() => {
    if (closingRef.current) return;
    closingRef.current = true;
    if (!reduced.current && panelRef.current && cardEl) {
      cardEl.style.visibility = 'visible';
      const first = panelRef.current.getBoundingClientRect();
      const last  = cardEl.getBoundingClientRect();
      cardEl.style.visibility = 'hidden';
      const dx = last.left - first.left, dy = last.top - first.top;
      const sx = last.width / first.width, sy = last.height / first.height;
      const anim = panelRef.current.animate([
        { transform: 'translate(0,0) scale(1,1)',                      opacity: 1, borderRadius: '20px' },
        { transform: `translate(${dx}px,${dy}px) scale(${sx},${sy})`, opacity: 0, borderRadius: '16px' },
      ], { duration: 320, easing: 'cubic-bezier(0.32,0.72,0,1)', fill: 'forwards' });
      overlayRef.current?.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 320, easing: 'ease', fill: 'forwards' });
      anim.onfinish = () => onClose();
    } else {
      onClose();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onClose]);

  useEffect(() => {
    const h = e => { if (e.key === 'Escape') doClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [doClose]);

  return (
    <div ref={overlayRef} className="chart-modal-overlay"
      onClick={e => e.target === overlayRef.current && doClose()}>
      <div ref={panelRef} className="chart-modal-panel"
        role="dialog" aria-modal="true" aria-labelledby="cmo-title">
        <div className="chart-modal-header">
          <span id="cmo-title" className="chart-modal-title-text">{title}</span>
          <button ref={closeRef} className="chart-modal-close-btn" onClick={doClose} aria-label="Fermer">
            <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24"><path d="M18 6L6 18M6 6l12 12"/></svg>
          </button>
        </div>
        <div className="chart-modal-body">{children}</div>
      </div>
    </div>
  );
}

// ── Chart config ──────────────────────────────────────────────────────────────
const CHART_CONFIGS = [
  { id: 'donut',      title: 'Répartition des dépenses',       subtitle: 'Par catégorie de dépense',   accentColor: '#EC4899', icon: <IconDonut />,    preview: <MiniDonut /> },
  { id: 'bar',        title: 'Revenus vs Dépenses',             subtitle: 'Mois par mois',              accentColor: '#F59E0B', icon: <IconBar />,      preview: <MiniBar /> },
  { id: 'line',       title: 'Évolution du solde',              subtitle: 'Courbe dans le temps',       accentColor: '#6366F1', icon: <IconLine />,     preview: <MiniLine /> },
  { id: 'heatmap',    title: 'Heatmap des dépenses',            subtitle: 'Intensité journalière',      accentColor: '#8B5CF6', icon: <IconGrid />,     preview: <MiniHeatmap /> },
  { id: 'daterange',  title: 'Comparaison par plages de dates', subtitle: 'Dépenses sur deux périodes', accentColor: '#10B981', icon: <IconCalendar />, preview: <MiniCompare /> },
  { id: 'comparison', title: 'Comparaison de périodes',         subtitle: 'Évolution par catégorie',    accentColor: '#3B82F6', icon: <IconArrows />,   preview: <MiniCompareBar /> },
];

// ── Charts page ───────────────────────────────────────────────────────────────
const Charts = () => {
  const [accounts, setAccounts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState('');
  const { transactions, loading, fetchTransactions } = useTransactions();
  const today = new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  const isDesktop = useIsDesktop();
  const [openChart, setOpenChart] = useState(null);

  useEffect(() => {
    if (!isDesktop && openChart) setOpenChart(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDesktop]);

  const loadForAccount = useCallback((accId) => {
    fetchTransactions({ accountId: accId, startDate: `${CURRENT_YEAR}-01-01`, endDate: `${CURRENT_YEAR}-12-31` });
  }, [fetchTransactions]);

  useEffect(() => {
    const init = async () => {
      try {
        const res = await accountsAPI.list();
        const accs = res?.data?.data || [];
        setAccounts(accs);
        const defaultAcc = accs.find(a => a.isDefault) || accs[0];
        if (defaultAcc) {
          setSelectedAccountId(defaultAcc._id);
          const [exp, inc] = await Promise.all([
            listCategories({ type: 'expense', accountId: defaultAcc._id }),
            listCategories({ type: 'income',  accountId: defaultAcc._id }),
          ]);
          setCategories([...exp, ...inc]);
          loadForAccount(defaultAcc._id);
        }
      } catch (e) { console.error(e); }
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAccountChange = async (accId) => {
    if (accId === selectedAccountId) return;
    setSelectedAccountId(accId);
    const [exp, inc] = await Promise.all([
      listCategories({ type: 'expense', accountId: accId }),
      listCategories({ type: 'income',  accountId: accId }),
    ]);
    setCategories([...exp, ...inc]);
    loadForAccount(accId);
  };

  const account = accounts.find(a => a._id === selectedAccountId);
  const currentMonth = String(new Date().getMonth() + 1).padStart(2, '0');
  const handleOpen  = useCallback((id, cardEl) => setOpenChart({ id, cardEl }), []);
  const handleClose = useCallback(() => setOpenChart(null), []);

  const renderChart = (id) => {
    switch (id) {
      case 'donut':      return <DonutChart accountId={selectedAccountId} categories={categories} />;
      case 'bar':        return <BarChart accountId={selectedAccountId} />;
      case 'line':       return <LineChart transactions={transactions} account={account} />;
      case 'heatmap':    return <HeatmapChart transactions={transactions} year={String(CURRENT_YEAR)} month={currentMonth} />;
      case 'daterange':  return <DateRangeComparisonChart accountId={selectedAccountId} categories={categories} />;
      case 'comparison': return <ComparisonChart accountId={selectedAccountId} categories={categories} />;
      default:           return null;
    }
  };

  return (
    <div className="page-content">
      <div className="page-header">
        <div>
          <h1 className="page-title">Graphiques</h1>
          <p className="page-sub">Analysez vos finances en un coup d'œil</p>
        </div>
        <div className="page-date">{today}</div>
      </div>

      {accounts.length > 0 && (
        <div className="acc-selector-grid">
          {accounts.map((a, i) => (
            <div key={a._id}
              className={`acc-selector-card${selectedAccountId === a._id ? ' active' : ''}`}
              onClick={() => handleAccountChange(a._id)}>
              <div className="account-top">
                <span className="account-dot" style={{ background: ACCOUNT_COLORS[i % ACCOUNT_COLORS.length] }} />
                <span className="account-bank">{a.bank || 'Banque'}</span>
                {a.isDefault && <span className="badge-default">Défaut</span>}
              </div>
              <div className="account-name">{a.label}</div>
            </div>
          ))}
        </div>
      )}

      {loading ? (
        <div className="loading-overlay"><div className="loading-spinner" /></div>
      ) : isDesktop ? (
        <div className="charts-card-grid">
          {CHART_CONFIGS.map(cfg => <ChartCard key={cfg.id} {...cfg} onOpen={handleOpen} />)}
        </div>
      ) : (
        <div className="charts-grid">
          <DonutChart accountId={selectedAccountId} categories={categories} />
          <BarChart accountId={selectedAccountId} />
          <LineChart transactions={transactions} account={account} />
          <HeatmapChart transactions={transactions} year={String(CURRENT_YEAR)} month={currentMonth} />
          <DateRangeComparisonChart accountId={selectedAccountId} categories={categories} />
          <ComparisonChart accountId={selectedAccountId} categories={categories} />
        </div>
      )}

      {openChart && (
        <ChartModal
          title={CHART_CONFIGS.find(c => c.id === openChart.id)?.title || ''}
          cardEl={openChart.cardEl}
          onClose={handleClose}>
          {renderChart(openChart.id)}
        </ChartModal>
      )}
    </div>
  );
};

export default Charts;
